import { useState } from 'react';
import { X, Send, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import type { SourceInfo } from '../../types';

interface SuggestEditModalProps {
  messageId: string;
  sources?: SourceInfo[];
  originalContent: string;
  onClose: () => void;
}

interface EditSuggestion {
  sourceId?: string;
  sourceTitle?: string;
  section?: string;
  currentText: string;
  suggestedText: string;
  reason: string;
  priority: 'low' | 'medium' | 'high';
}

export function SuggestEditModal({
  messageId,
  sources,
  originalContent,
  onClose,
}: SuggestEditModalProps) {
  const [selectedSource, setSelectedSource] = useState<SourceInfo | null>(null);
  const [currentText, setCurrentText] = useState('');
  const [suggestedText, setSuggestedText] = useState('');
  const [reason, setReason] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!suggestedText.trim() || !reason.trim()) {
      setError('Please provide both the suggested text and reason for the edit.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const suggestion: EditSuggestion = {
      sourceId: selectedSource?.path,
      sourceTitle: selectedSource?.title,
      section: selectedSource?.section,
      currentText,
      suggestedText,
      reason,
      priority,
    };

    try {
      // TODO: Backend endpoint /doc-processing/suggest-edit is not yet implemented.
      // For now, log locally and show a "saved locally" message to the user.
      console.warn('[SuggestEdit] Backend endpoint not implemented — saved locally:', {
        message_id: messageId,
        suggestion,
        original_response: originalContent,
      });

      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to submit suggestion. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center animate-fade-in">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Suggestion Saved Locally</h3>
          <p className="text-gray-500 mt-1">
            Your suggestion has been recorded. Backend submission is not yet available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="suggest-edit-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-primary-500" />
            <h3 id="suggest-edit-title" className="text-lg font-semibold text-gray-800">
              Suggest Documentation Edit
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Source selector */}
          {sources && sources.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Which document needs correction?
              </label>
              <div className="space-y-2">
                {sources.map((source, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSource(source)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedSource === source
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-800">{source.title}</p>
                    {source.section && (
                      <p className="text-xs text-gray-500 mt-0.5">{source.section}</p>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedSource(null)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedSource === null
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-800">General / AI Response</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    The AI's interpretation or response is incorrect
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Current text */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              What text is incorrect? (optional)
            </label>
            <textarea
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              placeholder="Copy the incorrect text here..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-300 outline-none resize-none"
              rows={3}
            />
          </div>

          {/* Suggested text */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              What should it say? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={suggestedText}
              onChange={(e) => setSuggestedText(e.target.value)}
              placeholder="Provide the correct information..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-300 outline-none resize-none"
              rows={4}
            />
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Why is this change needed? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why the current information is incorrect or misleading..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-300 outline-none resize-none"
              rows={2}
            />
          </div>

          {/* Priority */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Priority
            </label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    priority === p
                      ? p === 'high'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : p === 'medium'
                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                        : 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!suggestedText.trim() || !reason.trim() || isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="animate-pulse">Submitting...</span>
            ) : (
              <>
                <Send size={16} />
                Submit Suggestion
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
